import {
  Add as AddIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Slide,
  TextField,
  Typography
} from '@mui/material';
import React, { useState } from 'react';
import { api } from '../api/client';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const LEAD_SOURCES = {
  website: { label: 'Сайт', icon: '🌐' },
  ads: { label: 'Реклама', icon: '📢' },
  referral: { label: 'Рекомендация', icon: '👥' },
  cold_call: { label: 'Холодный звонок', icon: '📞' },
  event: { label: 'Мероприятие', icon: '🎪' },
  social: { label: 'Соц.сети', icon: '📱' },
  other: { label: 'Другое', icon: '❓' }
};

const QuickLeadCreate = ({ onLeadCreated, showAsFab = false }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: 'info', message: '' });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    source: 'website',
    notes: ''
  });

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: 'info', message: '' }), 4000);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      position: '',
      source: 'website',
      notes: ''
    });
    setAlert({ show: false, type: 'info', message: '' });
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    // Простая валидация
    if (!formData.name.trim()) {
      showAlert('error', 'Имя обязательно для заполнения');
      return;
    }

    if (!formData.email.trim() && !formData.phone.trim()) {
      showAlert('error', 'Укажите email или телефон');
      return;
    }

    try {
      setLoading(true);

      const response = await api.post('/leads', {
        ...formData,
        score: 50 // Базовый рейтинг для быстро созданных лидов
      });

      if (response.status === 201) {
        showAlert('success', 'Лид создан успешно!');

        // Закрываем диалог через 1.5 секунды
        setTimeout(() => {
          handleClose();
          if (onLeadCreated) {
            onLeadCreated(response.data);
          }
        }, 1500);
      }
    } catch (error) {
      showAlert('error', error.response?.data?.message || 'Ошибка создания лида');
    } finally {
      setLoading(false);
    }
  };

  if (showAsFab) {
    return (
      <>
        <Fab
          color="primary"
          aria-label="add lead"
          onClick={handleOpen}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
            '&:hover': {
              background: 'linear-gradient(45deg, #FE6B8B 60%, #FF8E53 100%)',
            }
          }}
        >
          <PersonAddIcon />
        </Fab>

        <Dialog
          open={open}
          onClose={handleClose}
          TransitionComponent={Transition}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{
            background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
            color: 'white'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonAddIcon />
              <Typography variant="h6">Быстрое создание лида</Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ mt: 2 }}>
            {alert.show && (
              <Alert severity={alert.type} sx={{ mb: 2 }}>
                {alert.message}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Имя *"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  autoFocus
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Телефон"
                  value={formData.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Компания"
                  value={formData.company}
                  onChange={(e) => handleFormChange('company', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Должность"
                  value={formData.position}
                  onChange={(e) => handleFormChange('position', e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Источник</InputLabel>
                  <Select
                    value={formData.source}
                    onChange={(e) => handleFormChange('source', e.target.value)}
                  >
                    {Object.entries(LEAD_SOURCES).map(([source, config]) => (
                      <MenuItem key={source} value={source}>
                        {config.icon} {config.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Заметки"
                  multiline
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  placeholder="Дополнительная информация о лиде..."
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleClose} disabled={loading}>
              Отмена
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
              startIcon={loading ? null : <AddIcon />}
            >
              {loading ? 'Создание...' : 'Создать лида'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Обычная кнопка для встраивания в интерфейс
  return (
    <>
      <Button
        variant="contained"
        startIcon={<PersonAddIcon />}
        onClick={handleOpen}
        sx={{
          background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
          '&:hover': {
            background: 'linear-gradient(45deg, #FE6B8B 60%, #FF8E53 100%)',
          }
        }}
      >
        Создать лида
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Создать нового лида</DialogTitle>

        <DialogContent>
          {alert.show && (
            <Alert severity={alert.type} sx={{ mb: 2 }}>
              {alert.message}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Имя *"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Телефон"
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Компания"
                value={formData.company}
                onChange={(e) => handleFormChange('company', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Должность"
                value={formData.position}
                onChange={(e) => handleFormChange('position', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Источник</InputLabel>
                <Select
                  value={formData.source}
                  onChange={(e) => handleFormChange('source', e.target.value)}
                >
                  {Object.entries(LEAD_SOURCES).map(([source, config]) => (
                    <MenuItem key={source} value={source}>
                      {config.icon} {config.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Заметки"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Создание...' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuickLeadCreate;